import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import SwasthyaLogo from "../../assets/img/clients/Swasthya logo.png";
import "../../assets/css/Cartattva.css";

// Adjust this path to wherever analytics.js actually lives
import { trackCaseStudyView, trackWhatsAppClick } from "../../utils/analytics";

/* =========================================================
   SWASTHYA FEATURES
========================================================= */

const features = [
  {
    title: "Patient Management",
    text: "Centralized patient records with complete visibility into patient history, injuries, treatment sessions and appointments.",
  },
  {
    title: "Multiple Injury Management",
    text: "A single patient can have multiple injuries, with each injury managed independently throughout the treatment lifecycle.",
  },
  {
    title: "Separate Treatment Sessions",
    text: "Each injury can have its own treatment session with a dedicated treatment plan, prescription and appointment schedule.",
  },
  {
    title: "Treatment Plan Management",
    text: "Doctors can define treatment plans based on the patient's injury and required physiotherapy services.",
  },
  {
    title: "Automatic Appointment Scheduling",
    text: "Appointments are automatically scheduled according to the configured treatment plan, reducing manual scheduling work.",
  },
  {
    title: "Centralized Calendar",
    text: "A complete calendar displays scheduled appointments and provides a clear view of the clinic's daily treatment workload.",
  },
  {
    title: "Doctor-wise Appointment Filtering",
    text: "Appointments can be filtered according to the assigned doctor, making it easier for physiotherapists to manage their schedules.",
  },
  {
    title: "Rehab Information Tracking",
    text: "Doctors can record and update rehabilitation information multiple times throughout the patient's treatment journey.",
  },
  {
    title: "Assigned Service Visibility",
    text: "During an appointment, doctors can view and select only the services assigned to that appointment, reducing confusion and miscommunication.",
  },
  {
    title: "Prescription Management",
    text: "Treatment-specific prescriptions can be maintained separately for each treatment session and updated when required.",
  },
  {
    title: "Patient Progress Tracking",
    text: "Doctors can monitor patient progress throughout the treatment lifecycle and maintain structured treatment information.",
  },
  {
    title: "Clinic Statistics & Reporting",
    text: "Management dashboards and statistics provide visibility into clinic activity, treatments and patient operations.",
  },
];

/* =========================================================
   BUSINESS IMPACT
========================================================= */

const impact = [
  "Centralized patient and treatment information",
  "Multiple injuries can be managed independently for the same patient",
  "Separate treatment sessions for each injury",
  "Automated appointment scheduling based on treatment plans",
  "Reduced manual appointment scheduling",
  "Clear doctor-wise appointment management",
  "Improved communication between clinic staff and doctors",
  "Better tracking of rehabilitation progress",
  "Treatment-specific prescription management",
  "Improved visibility into clinic operations",
];

/* =========================================================
   TECHNOLOGY
========================================================= */

const technology = [
  "ASP.NET MVC",
  "SQL Server",
  "REST APIs",
];

/* =========================================================
   SCREENSHOT TITLES
========================================================= */
/* =========================================================
   SCREENSHOT ORDER
========================================================= */

const screenshotOrder = [
  "Calender.png",
  "Instructions.png",
  "Patient Progress.png",
  "Prescription.png",
  "PT Treatement Dashboard.png",
  "Rehab Info.png",
  "Statistics.png",
  "Treatement.png",
];


const screenshotTitles = {
  "Calender.png": "Appointment Calendar",
  "Instructions.png": "Treatment Instructions",
  "Patient Progress.png": "Patient Progress",
  "Prescription.png": "Prescription Management",
  "PT Treatement Dashboard.png": "PT Treatment Dashboard",
  "Rehab Info.png": "Rehabilitation Information",
  "Statistics.png": "Clinic Statistics",
  "Treatement.png": "Treatment Management",
};

/* =========================================================
   SWASTHYA SCREENSHOTS
   VITE / REACT
========================================================= */
const swasthyaImages = require.context(
  "../../assets/img/Swasthya",
  false,
  /\.(png|jpe?g|jpeg|webp|gif)$/i
);

const screenshotData = swasthyaImages
  .keys()
  .map((file) => {
    const fileName = file.replace("./", "");

    return {
      fileName,
      image: swasthyaImages(file),
      title:
        screenshotTitles[fileName] ||
        fileName
          .replace(/\.[^/.]+$/, "")
          .replace(/[-_]/g, " "),
    };
  })
  .sort((a, b) => {
    const aIndex = screenshotOrder.indexOf(a.fileName);
    const bIndex = screenshotOrder.indexOf(b.fileName);

    if (aIndex === -1 && bIndex === -1) {
      return a.fileName.localeCompare(b.fileName);
    }

    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });

/* =========================================================
   SEO
========================================================= */

function useSwasthyaSEO() {
  useEffect(() => {
    const title =
      "Swasthya – Physiotherapy Clinic Management System | HexxaTech Softwares";

    const description =
      "Swasthya is a custom physiotherapy clinic management system developed by HexxaTech Softwares for patient management, injury tracking, treatment sessions, prescriptions, rehabilitation, appointments and clinic operations.";

    const canonical =
      "https://hexxatech.co.in/case-studies/swasthya";

    document.title = title;

    const setMeta = (name, content) => {
      let element = document.querySelector(
        `meta[name="${name}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    const setProperty = (property, content) => {
      let element = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setMeta("description", description);

    setMeta(
      "keywords",
      "Swasthya, physiotherapy clinic management software, physiotherapy management system, physiotherapy software, patient management software, treatment management software, appointment scheduling software, rehabilitation management software, physiotherapy clinic software Pune, healthcare software Pune, HexxaTech Softwares"
    );

    setProperty("og:title", title);
    setProperty("og:description", description);
    setProperty("og:url", canonical);
    setProperty("og:type", "article");
    setProperty("og:site_name", "HexxaTech Softwares");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    let canonicalElement = document.querySelector(
      'link[rel="canonical"]'
    );

    if (!canonicalElement) {
      canonicalElement = document.createElement("link");
      canonicalElement.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalElement);
    }

    canonicalElement.setAttribute("href", canonical);

    /* =====================================================
       STRUCTURED DATA
    ===================================================== */

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline:
        "Swasthya – Physiotherapy Clinic Management System",
      description,
      url: canonical,
      author: {
        "@type": "Organization",
        name: "HexxaTech Softwares",
        url: "https://hexxatech.co.in",
      },
      publisher: {
        "@type": "Organization",
        name: "HexxaTech Softwares",
        url: "https://hexxatech.co.in",
      },
      about: {
        "@type": "SoftwareApplication",
        name: "Swasthya",
        applicationCategory:
          "HealthApplication",
        operatingSystem: "Web",
        description:
          "Custom physiotherapy clinic management software for patient, injury, treatment, appointment and rehabilitation management.",
      },
    };

    let schema = document.getElementById(
      "swasthya-case-study-schema"
    );

    if (!schema) {
      schema = document.createElement("script");
      schema.type = "application/ld+json";
      schema.id = "swasthya-case-study-schema";
      document.head.appendChild(schema);
    }

    schema.textContent =
      JSON.stringify(structuredData);

    return () => {
      const existingSchema = document.getElementById(
        "swasthya-case-study-schema"
      );

      if (existingSchema) {
        existingSchema.remove();
      }
    };
  }, []);
}

/* =========================================================
   COMPONENT
========================================================= */

export default function Swasthya() {
  useSwasthyaSEO();

  // GA4: record a case-study-view event when this page mounts
  useEffect(() => {
    trackCaseStudyView("Swasthya");
  }, []);

  return (
    <main className="cartattva-page swasthya-page">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="cartattva-hero">

        <div className="cartattva-hero-glow cartattva-glow-one" />
        <div className="cartattva-hero-glow cartattva-glow-two" />

        <div className="cartattva-container">

          {/* BACK */}
          <div className="cartattva-back">
            <Link to="/case-studies">
              ← Back to Case Studies
            </Link>
          </div>

          <div className="cartattva-hero-content">

            {/* LOGO */}

            <div className="cartattva-logo-wrapper swasthya-logo-wrapper">
              <img
                src={SwasthyaLogo}
                alt="Swasthya Physiotherapy Clinic Management System"
                className="cartattva-logo swasthya-logo"
              />
            </div>

            {/* EYEBROW */}

            <div className="cartattva-eyebrow">

              <span className="cartattva-eyebrow-dot" />

              Healthcare Case Study

            </div>

            {/* H1 */}

            <h1>
              Physiotherapy Clinic
              <br />
              Management System
            </h1>

            <div className="cartattva-title-line" />

            <h2>
              A custom clinic management platform
              designed specifically for physiotherapy
              clinics.
            </h2>

            <p>
              Swasthya centralizes patient management,
              multiple injuries, treatment sessions,
              treatment plans, prescriptions,
              rehabilitation, appointments and clinic
              operations into one connected system.
            </p>

            {/* TAGS */}

            <div className="cartattva-hero-tags">

              <span>
                Physiotherapy Focused
              </span>

              <span>
                Patient & Injury Management
              </span>

              <span>
                Treatment Management
              </span>

              <span>
                Appointment Scheduling
              </span>

              <span>
                ASP.NET MVC
              </span>

              <span>
                SQL Server
              </span>

            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          OVERVIEW
      ===================================================== */}

      <section className="cartattva-overview">

        <div className="cartattva-container">

          <div className="cartattva-section-label">
            Overview
          </div>

          <h2 className="cartattva-section-title">
            Designed around the way physiotherapy
            clinics work.
          </h2>

          <div className="cartattva-overview-grid">

            {/* CHALLENGE */}

            <article className="cartattva-info-card">

              <span>
                THE CHALLENGE
              </span>

              <h3>
                Physiotherapy treatment is more than
                a simple appointment workflow.
              </h3>

              <p>
                A single patient may have multiple
                injuries, and each injury can require
                its own treatment session, treatment
                plan, prescription and appointment
                schedule.
              </p>

              <p>
                The clinic needed a system that could
                maintain these relationships while
                making appointment management simple
                for doctors and clinic staff.
              </p>

            </article>

            {/* SOLUTION */}

            <article className="cartattva-info-card solution">

              <span>
                OUR SOLUTION
              </span>

              <h3>
                One connected platform for the
                complete treatment journey.
              </h3>

              <p>
                HexxaTech developed Swasthya as a
                physiotherapy-focused clinic management
                system connecting patients, injuries,
                treatment sessions, treatment plans,
                prescriptions and appointments.
              </p>

              <p>
                Treatment plans can generate appointment
                schedules while doctors can filter
                appointments, access assigned services
                and update rehabilitation information
                throughout the treatment journey.
              </p>

            </article>

          </div>

        </div>

      </section>

      {/* =====================================================
          WORKFLOW
      ===================================================== */}

      <section className="swasthya-workflow">

        <div className="cartattva-container">

          <div className="cartattva-section-label">
            Treatment Workflow
          </div>

          <h2 className="cartattva-section-title">
            From patient registration to treatment
            progress.
          </h2>

          <div className="swasthya-workflow-grid">

            <article className="swasthya-workflow-card">

              <span className="swasthya-workflow-number">
                01
              </span>

              <div>
                <span className="swasthya-workflow-label">
                  Patient → Injury
                </span>

                <h3>
                  Multiple injuries under one patient
                </h3>

                <p>
                  Each patient profile can contain
                  multiple injuries, allowing doctors
                  to maintain separate treatment
                  information for each condition.
                </p>
              </div>

            </article>

            <article className="swasthya-workflow-card">

              <span className="swasthya-workflow-number">
                02
              </span>

              <div>
                <span className="swasthya-workflow-label">
                  Injury → Treatment Session
                </span>

                <h3>
                  Independent treatment sessions
                </h3>

                <p>
                  Every injury can have its own
                  treatment session with an independent
                  treatment plan, prescription and
                  appointment schedule.
                </p>
              </div>

            </article>

            <article className="swasthya-workflow-card">

              <span className="swasthya-workflow-number">
                03
              </span>

              <div>
                <span className="swasthya-workflow-label">
                  Treatment Plan → Appointments
                </span>

                <h3>
                  Automated appointment planning
                </h3>

                <p>
                  Once a treatment plan is defined,
                  appointments can be scheduled based
                  on the required treatment frequency
                  and assigned services.
                </p>
              </div>

            </article>

            <article className="swasthya-workflow-card">

              <span className="swasthya-workflow-number">
                04
              </span>

              <div>
                <span className="swasthya-workflow-label">
                  Appointment → Progress
                </span>

                <h3>
                  Treatment progress at every visit
                </h3>

                <p>
                  Doctors can access assigned services,
                  update treatment information and
                  record rehabilitation progress during
                  appointments.
                </p>
              </div>

            </article>

          </div>

        </div>

      </section>

      {/* =====================================================
          FEATURES
      ===================================================== */}

      <section className="cartattva-features">

        <div className="cartattva-container">

          <div className="cartattva-section-label">
            Key Features
          </div>

          <h2 className="cartattva-section-title">
            Built specifically for physiotherapy
            workflows.
          </h2>

          <div className="cartattva-feature-grid">

            {features.map((feature, index) => (

              <article
                className="cartattva-feature-card"
                key={feature.title}
              >

                <span className="feature-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h3>
                  {feature.title}
                </h3>

                <p>
                  {feature.text}
                </p>

              </article>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          TECHNOLOGY
      ===================================================== */}

      <section className="cartattva-technology">

        <div className="cartattva-container">

          <div className="cartattva-section-label">
            Technology
          </div>

          <h2 className="cartattva-section-title">
            What it's built on.
          </h2>

          <p className="technology-description">
            Swasthya was developed using a reliable
            web application architecture designed for
            structured healthcare workflows,
            centralized data management and API-based
            communication.
          </p>

          <div className="technology-list">

            {technology.map((item) => (

              <span key={item}>
                {item}
              </span>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          BUSINESS IMPACT
      ===================================================== */}

      <section className="cartattva-impact">

        <div className="cartattva-container">

          <div className="cartattva-section-label">
            Business Impact
          </div>

          <h2 className="cartattva-section-title">
            What changed for the clinic.
          </h2>

          <div className="impact-grid">

            {impact.map((item) => (

              <div
                className="impact-item"
                key={item}
              >

                <span className="impact-check">
                  ✓
                </span>

                <span>
                  {item}
                </span>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          SCREENSHOTS
      ===================================================== */}

      <section className="cartattva-screenshots">

        <div className="cartattva-container">

          <div className="cartattva-section-label">
            Screenshots
          </div>

          <h2 className="cartattva-section-title">
            See Swasthya in action.
          </h2>

          <p className="screenshots-intro">
            A look at the key screens used by doctors
            and clinic staff to manage appointments,
            treatment sessions, prescriptions,
            rehabilitation and patient progress.
          </p>

          {screenshotData.length > 0 ? (

            <div className="screenshots-grid">

              {screenshotData.map((item) => (

                <figure
                  className="screenshot-card"
                  key={item.fileName}
                >

                  <div className="screenshot-image-wrapper">

                    <img
                      src={item.image}
                      alt={`${item.title} - Swasthya Physiotherapy Clinic Management System`}
                      loading="lazy"
                    />

                  </div>

                  <figcaption>

                    <strong>
                      {item.title}
                    </strong>

                    <span>
                      Swasthya Clinic Management System
                    </span>

                  </figcaption>

                </figure>

              ))}

            </div>

          ) : (

            <div className="no-screenshots">
              Swasthya screenshots are currently
              unavailable.
            </div>

          )}

        </div>

      </section>

      {/* =====================================================
          RELATED SERVICES
      ===================================================== */}

      <section className="cartattva-overview">

        <div className="cartattva-container">

          <div className="cartattva-section-label">
            Related Services
          </div>

          <h2 className="cartattva-section-title">
            Services used alongside this healthcare workflow.
          </h2>

          <div className="cartattva-feature-grid">

            <article className="cartattva-feature-card">
              <h3>Healthcare Software</h3>
              <p>Patient records, appointments, medical workflows and reporting built around actual clinic operations.</p>
              <Link to="/services/healthcare-software">Explore Healthcare Software →</Link>
            </article>

            <article className="cartattva-feature-card">
              <h3>Custom Software Development</h3>
              <p>Turn department processes, approvals and business rules into a connected digital system.</p>
              <Link to="/services/custom-software-development">Explore Custom Software Development →</Link>
            </article>

            <article className="cartattva-feature-card">
              <h3>Mobile App Development</h3>
              <p>Support field teams, patient-facing operations and real-time documentation through mobile workflows.</p>
              <Link to="/services/mobile-app-development">Explore Mobile App Development →</Link>
            </article>

          </div>

        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="cartattva-cta">

        <div className="cartattva-cta-glow" />

        <div className="cartattva-container">

          <div className="cartattva-cta-inner">

            <span className="cta-label">
              Custom Healthcare Software
            </span>

            <h2>
              Have a healthcare workflow
              <br />
              that software can simplify?
            </h2>

            <p>
              Tell us about your clinic, treatment
              workflow, operational challenges and
              goals. We'll help you turn them into a
              practical software solution designed
              around the way your healthcare business
              actually works.
            </p>

            <a
              href="https://api.whatsapp.com/send/?phone=919226591664&text=Hello+HexxaTech%2C+I+would+like+to+discuss+a+healthcare+software+project.&type=phone_number&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              className="cartattva-cta-button"
              onClick={() => trackWhatsAppClick("swasthya_case_study_cta")}
            >
              Discuss Your Project

              <span>
                →
              </span>
            </a>

          </div>

        </div>

      </section>

      {/* =====================================================
          FOOTER NOTE
      ===================================================== */}

      <div className="cartattva-footer-note">
        Developed by HexxaTech Softwares
      </div>

    </main>
  );
}
